// src/components/UserFiles/UserFilesUploadSection.jsx

import React, { useState } from 'react';
import { Box, Button, Typography, CircularProgress, Stack } from '@mui/material';
import { CloudUploadOutlined, InsertDriveFileOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { uploadUserFile } from '../../services/userFileService';

const UserFilesUploadSection = ({ t, user, onFilesChanged }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !user) return;

    setUploading(true);
    try {
      const profileId = user.profile_id;
      const res = await uploadUserFile(profileId, 'anonymization', selectedFile);

      if (res.success) {
        enqueueSnackbar(t('files.upload_success', 'Súbor bol úspešne nahraný.'), {
          variant: 'success',
        });
        setSelectedFile(null);
        if (onFilesChanged) {
          await onFilesChanged();
        }
      } else {
        const backendErr = res.errors || {};
        const msg =
          backendErr.detail ||
          t('files.upload_failed', 'Nepodarilo sa nahrať súbor.');
        enqueueSnackbar(msg, { variant: 'error' });
      }
    } catch (e) {
      console.error('Upload error', e);
      const msg = t('files.upload_failed', 'Nepodarilo sa nahrať súbor.');
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
        {t('files.upload_section_title', 'Nahrať nový súbor')}
      </Typography>

      <Box
        sx={{
          p: 2,
          borderRadius: 3,
          border: '1px dashed',
          borderColor: 'primary.300',
          bgcolor: 'primary.50',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <Button
            variant="outlined"
            component="label"
            startIcon={<InsertDriveFileOutlined />}
            sx={{ borderRadius: 2, fontWeight: 600, bgcolor: 'background.paper', borderColor: 'primary.300' }}
          >
            {t('files.choose_file', 'Vybrať súbor')}
            <input type="file" hidden onChange={handleFileChange} />
          </Button>

          <Box sx={{ flexGrow: 1, minWidth: 0, px: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: selectedFile ? 'primary.900' : 'text.secondary',
                fontWeight: selectedFile ? 600 : 400,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {selectedFile
                ? selectedFile.name
                : t('files.no_file_selected', 'Nie je vybraný žiadny súbor')}
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            disableElevation
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlined />}
            sx={{ borderRadius: 2, fontWeight: 600, minWidth: 120 }}
          >
            {uploading ? t('common.uploading', 'Nahráva sa...') : t('common.upload', 'Nahrať')}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default UserFilesUploadSection;